import {
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Slider,
} from '@mui/material';
import { FieldRadioGroup } from '../../form';

interface QuestionRendererProps {
  question: any;
  value: any;
  otherTexts: Record<string, string>;
  onMultiSelectChange: (
    questionId: string,
    value: string,
    checked: boolean,
  ) => void;
  onSingleSelectChange: (questionId: string, value: string) => void;
  onNumberChange: (questionId: string, value: number) => void;
  onOtherTextChange: (questionId: string, value: string) => void;
  shouldShowQuestion: (question: any) => boolean;
}

export const QuestionRenderer = ({
  question,
  value,
  otherTexts,
  onMultiSelectChange,
  onSingleSelectChange,
  onNumberChange,
  onOtherTextChange,
  shouldShowQuestion,
}: QuestionRendererProps) => {
  if (!shouldShowQuestion(question)) {
    return null;
  }

  switch (question.type) {
    case 'multi-select':
      return (
        <Box
          key={question.id}
          sx={{
            bgcolor: 'var(--mui-palette-background-level1)',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1">{question.question}</Typography>
            {question.helperText && (
              <Typography variant="body2" color="text.secondary">
                {question.helperText}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Select all that apply
            </Typography>
            <Stack spacing={1}>
              {question.options?.map((option: any) => (
                <Box key={option.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          Array.isArray(value) && value.includes(option.value)
                        }
                        onChange={(e) =>
                          onMultiSelectChange(
                            question.id,
                            option.value,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={option.label}
                  />
                  {option.additionalInfo &&
                    Array.isArray(value) &&
                    value.includes(option.value) && (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Please specify..."
                        value={otherTexts[question.id] || ''}
                        onChange={(e) =>
                          onOtherTextChange(question.id, e.target.value)
                        }
                        sx={{ ml: 4, mt: 1 }}
                      />
                    )}
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>
      );

    case 'single-select':
      return (
        <Box
          key={question.id}
          sx={{
            bgcolor: 'var(--mui-palette-background-level1)',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1">{question.question}</Typography>
            {question.helperText && (
              <Typography variant="body2" color="text.secondary">
                {question.helperText}
              </Typography>
            )}
            <FieldRadioGroup
              field={{
                value: value || '',
                handleValueChange: (newValue: string) =>
                  onSingleSelectChange(question.id, newValue),
                label: '',
                name: question.id,
              }}
              options={
                question.options?.map((opt: any) => ({
                  label: opt.label,
                  value: opt.value,
                })) || []
              }
              vertical
            />
            {question.options?.find(
              (opt: any) => opt.additionalInfo && opt.value === value,
            ) && (
              <TextField
                fullWidth
                size="small"
                placeholder="Please specify..."
                value={otherTexts[question.id] || ''}
                onChange={(e) => onOtherTextChange(question.id, e.target.value)}
              />
            )}
          </Stack>
        </Box>
      );

    case 'number':
      return (
        <Box
          key={question.id}
          sx={{
            bgcolor: 'var(--mui-palette-background-level1)',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1">{question.question}</Typography>
            {question.helperText && (
              <Typography variant="body2" color="text.secondary">
                {question.helperText}
              </Typography>
            )}
            {question.showSlider ? (
              <Box sx={{ px: 2 }}>
                <Slider
                  value={value || 0}
                  onChange={(_, newValue) =>
                    onNumberChange(question.id, newValue as number)
                  }
                  min={question.min || 0}
                  max={question.max || 100}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
            ) : (
              <TextField
                type="number"
                value={value || ''}
                onChange={(e) =>
                  onNumberChange(question.id, parseInt(e.target.value, 10) || 0)
                }
                slotProps={{
                  htmlInput: { min: question.min, max: question.max },
                }}
              />
            )}
          </Stack>
        </Box>
      );

    default:
      return null;
  }
};
